import React, { useContext, useState, useEffect } from "react";
import { Modal, Form, InputNumber, Input, message, Alert } from "antd";
import { useTranslation } from "react-i18next";
import DeliveryWorkerContext from "../../../context/delivery-worker/context";

const { TextArea } = Input;

interface IRefundInsuranceModalProps {
  visible: boolean;
  onClose: () => void;
  workerId: number;
  remainingAmount: number;
  existingNote?: string;
}

const RefundInsuranceModal: React.FC<IRefundInsuranceModalProps> = ({
  visible,
  onClose,
  workerId,
  remainingAmount,
  existingNote,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { actions, loading } = useContext(DeliveryWorkerContext);
  const [submitting, setSubmitting] = useState(false);

  // Set existing note when modal opens
  useEffect(() => {
    if (visible && existingNote) {
      form.setFieldsValue({ insuranceRefundedNote: existingNote });
    }
  }, [visible, existingNote, form]);

  const handleConfirm = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const success = await actions.refundInsurance(workerId, {
        refundedInsuranceAmount: values.refundedInsuranceAmount,
        insuranceRefundedNote: values.insuranceRefundedNote || undefined,
      });

      if (success) {
        message.success(t("insurance_refunded_successfully"));
        form.resetFields();
        onClose();
      }
    } catch (error) {
      console.error("Failed to refund insurance:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={t("refund_insurance")}
      open={visible}
      onOk={handleConfirm}
      onCancel={handleCancel}
      confirmLoading={submitting || loading.includes("refundInsurance")}
      okText={t("confirm")}
      cancelText={t("cancel")}
      width={450}
      destroyOnClose
    >
      <Alert
        type="info"
        message={`${t("remaining_insurance_amount")}: ${remainingAmount}`}
        style={{ marginBottom: 16 }}
      />

      <Form form={form} layout="vertical">
        <Form.Item
          name="refundedInsuranceAmount"
          label={t("refund_amount")}
          rules={[
            { required: true, message: t("required") },
            { type: "number", min: 0.01, message: t("min_value_error") },
            {
              type: "number",
              max: remainingAmount,
              message: t("max_value_error"),
            },
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            placeholder={t("refund_amount")}
            min={0.01}
            max={remainingAmount}
            precision={2}
          />
        </Form.Item>

        <Form.Item
          name="insuranceRefundedNote"
          label={t("insurance_refunded_note")}
        >
          <TextArea
            rows={3}
            placeholder={t("insurance_refunded_note")}
            maxLength={1000}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default RefundInsuranceModal;
